<template>
  <div id="cesium-container" ref="cesiumContainer">
    <div :id="'infobox' + id" class="infobox" ref="infobox">Loading ...</div>
    <div id="blurred-background" v-if="isViewerLoading || loading">
      <v-progress-circular
        id="loading-indicator"
        :size="40"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
    <cesium-recorder ref="cesiumRecorder" />
    <cesium-lighting-button ref="cesiumLightingButton" />
    <OverlayComponent :elementToOverlay="timelineContainer"
      ><CesiumMarkerOverlay
        :startDate="dates[0]"
        :endDate="dates[dates.length - 1]"
        :markers="markers"
        v-on:changeDate="setTimeFromUTC($event)"
    /></OverlayComponent>
  </div>
</template>

<script>
import { CesiumInitialization } from '../../components/ThreeD/CesiumInitialization'
import { AnalyticalOrbit } from '../../components/ThreeD/AnalyticalOrbit'
import CesiumRecorder from '../../components/ThreeD/CesiumRecorder.vue'
import CesiumLightingButton from '../../components/ThreeD/CesiumLightingButton.vue'
import CesiumTools from '../../components/ThreeD/CesiumTools'
import CesiumConstellationsTools from '../../components/ThreeD/CesiumConstellationsTools'
import * as Cesium from 'cesium'
import CesiumMarkerOverlay from './CesiumMarkerOverlay'
import OverlayComponent from './OverlayComponent'
import { mapGetters } from 'vuex'

export default {
  name: 'EvasiveManeuverCesiumVisualizer',
  mixins: [CesiumInitialization, AnalyticalOrbit],
  props: {
    loading: { type: Boolean, default: false },
    maneuveringTargetPositions: {
      type: Array,
      required: true
    },
    chaserPositions: {
      type: Array,
      required: true
    },
    nominalTargetPositions: {
      type: Array,
      default: null
    },
    dates: {
      type: Array,
      required: true
    },
    id: {
      type: String
    },
    targetManeuverDates: {
      type: Array,
      default: () => []
    },
    chaserManeuverDates: {
      type: Array,
      default: () => []
    },
    satelliteWithSphereIds: {
      type: Array,
      default: () => []
    }
  },
  components: {
    'cesium-recorder': CesiumRecorder,
    'cesium-lighting-button': CesiumLightingButton,
    CesiumMarkerOverlay: CesiumMarkerOverlay,
    OverlayComponent: OverlayComponent
  },
  data() {
    return {
      isViewerLoading: false,
      satellitesDS: null,
      removeEventListenerCallback: null,
      timelineContainer: null
    }
  },
  computed: {
    ...mapGetters({
      currentMission: 'currentMission'
    }),
    satelliteIDs() {
      if (this.nominalTargetPositions == null) {
        return {
          CHASER: 'Chaser',
          MANEUVERING_TARGET: 'Target'
        }
      } else {
        return {
          CHASER: 'Chaser',
          MANEUVERING_TARGET: 'Target (with maneuver)',
          NOMINAL_TARGET: 'Target (Nominal)'
        }
      }
    },
    minimumApproachDistance() {
      return this.currentMission?.minimumApproachDistance ?? 0.1
    },
    markers() {
      const targetMarkers = this.targetManeuverDates.map((date) => ({
        color: 'blue',
        tooltip: 'Target burn',
        date
      }))

      const chaserMarkers = this.chaserManeuverDates.map((date) => ({
        color: 'red',
        tooltip: 'Chaser burn',
        date
      }))

      return [...targetMarkers, ...chaserMarkers]
    },
    julianDates() {
      return this.dates.map((sampleTime) =>
        Cesium.JulianDate.fromIso8601(sampleTime)
      )
    },
    julianStartDate() {
      return Cesium.JulianDate.fromIso8601(this.dates[0])
    },
    julianEndDate() {
      return Cesium.JulianDate.fromIso8601(this.dates[this.dates.length - 1])
    },
    maneuverIndex() {
      if (this.maneuveringTargetPositions != null) {
        let i = 0
        while (
          i < this.maneuveringTargetPositions.length &&
          this.isEqualPosition(
            this.maneuveringTargetPositions[i],
            this.nominalTargetPositions[i],
            0.001
          )
        ) {
          i += 1
        }
        return i !== this.maneuveringTargetPositions.length ? i : -1
      }
    },
    afterManeuverTargetPositions() {
      if (this.maneuveringTargetPositions != null && this.maneuverIndex > -1) {
        return this.maneuveringTargetPositions.slice(this.maneuverIndex)
      }
      return []
    },
    afterManeuverJulianDates() {
      if (this.julianDates != null && this.maneuverIndex > -1) {
        return this.julianDates.slice(this.maneuverIndex)
      }
      return []
    }
  },
  watch: {
    dialogState() {
      if (this.dialogState) {
        this.$nextTick(() => {
          this.initViewer()
        })
      } else {
        this.viewer = null
      }
    },
    maneuveringTargetPositions() {
      this.resetSatellites()
      this.resetClockEventListener()
    },
    chaserPositions() {
      this.resetSatellites()
      this.resetClockEventListener()
    },
    nominalTargetPositions() {
      this.resetSatellites()
      this.resetClockEventListener()
    },
    dates() {
      this.resetSatellites()
      this.resetClockEventListener()
    },
    satelliteIDs: {
      handler(newValue) {
        this.$emit('updateSatelliteIds', Object.values(newValue))
      },
      immediate: true
    }
  },
  async mounted() {
    this.isViewerLoading = true
    this.initializeViewer(true, true, false, false, false, true)
    this.insertElementInToolbar(this.$refs.cesiumLightingButton.$el)
    this.insertElementInToolbar(this.$refs.cesiumRecorder.$el)

    this.satellitesDS = new Cesium.CustomDataSource()
    this.viewer.dataSources.add(this.satellitesDS)

    this.viewer.clock.shouldAnimate = true
    this.setCesiumClock(this.julianStartDate, this.julianEndDate)
    this.viewer.clock.onTick.addEventListener(this.computeIcrfToFixed)
    this.computeIcrfToFixed(this.viewer.clock, true)
    this.viewer.scene.render(this.viewer.clock.currentTime)
    this.initSatellites()
    this.initClockEventListener()
    this.spherePrimitives = []
    this.timelineContainer = this.viewer.timeline.container
    this.isViewerLoading = false
  },
  beforeDestroy() {},
  methods: {
    initMarkers() {
      const { x, y, width, height } =
        this.viewer.timeline.container.getBoundingClientRect()
      const containerRect = this.$refs.cesiumContainer?.getBoundingClientRect()
      if (containerRect) {
        const x2 = containerRect.x,
          y2 = containerRect.y
        this.posX = x - x2
        this.posY = y - y2
        this.width = width
        this.height = height
      }
    },
    initClockEventListener() {
      this.removeEventListenerCallback =
        this.viewer.clock.onTick.addEventListener(function (clock) {
          this.updateInfoBox(clock)
          this.updateSphere(clock)
        }, this)
    },
    updateInfoBox(clock) {
      const infoBox = this.$refs.infobox
      let displayText = ''
      if (infoBox != null) {
        const chaserPosition = this.satellitesDS.entities
          .getById(this.satelliteIDs.CHASER)
          ?.position?.getValue(clock.currentTime)
        const maneuveringTargetPosition = this.satellitesDS.entities
          .getById(this.satelliteIDs.MANEUVERING_TARGET)
          ?.position?.getValue(clock.currentTime)
        const nominalTargetPosition = this.satellitesDS.entities
          .getById(this.satelliteIDs.NOMINAL_TARGET || '')
          ?.position?.getValue(clock.currentTime)

        if (chaserPosition != null && maneuveringTargetPosition != null) {
          const chaserToManeuveringDistance = Cesium.Cartesian3.distance(
            chaserPosition,
            maneuveringTargetPosition
          )

          displayText += `Chaser to maneuvering target distance: ${(
            chaserToManeuveringDistance / 1000
          ).toFixed(3)} km<br>`
        }

        if (chaserPosition != null && nominalTargetPosition != null) {
          const chaserToNominalDistance = Cesium.Cartesian3.distance(
            chaserPosition,
            nominalTargetPosition
          )
          displayText += `Chaser to nominal target distance: ${(
            chaserToNominalDistance / 1000
          ).toFixed(3)} km<br>`
        }
        infoBox.innerHTML = displayText
      }
    },
    updateSphere(clock) {
      if (this.spherePrimitives?.length) {
        for (const [index, primitive] of this.spherePrimitives.entries()) {
          this.viewer.scene.primitives.remove(primitive)
          this.spherePrimitives[index] = null
        }
        this.spherePrimitives = []
      }
      if (this.satelliteWithSphereIds?.length) {
        for (const satelliteId of this.satelliteWithSphereIds) {
          const position = this.satellitesDS.entities
            .getById(satelliteId)
            ?.position?.getValue(clock.currentTime)

          if (position != null) {
            this.spherePrimitives.push(
              new Cesium.EllipsoidPrimitive({
                center: position,
                radii: new Cesium.Cartesian3(
                  this.minimumApproachDistance,
                  this.minimumApproachDistance,
                  this.minimumApproachDistance
                )
              })
            )
            this.viewer.scene.primitives.add(
              this.spherePrimitives[this.spherePrimitives.length - 1]
            )
          }
        }
      }
    },
    resetClockEventListener() {
      if (this.removeEventListenerCallback != null) {
        this.removeEventListenerCallback()
      }
      this.initClockEventListener()
    },
    initSatellites() {
      this.satellitesDS.entities.add(
        this.initSatEntity(
          this.chaserPositions,
          this.julianDates,
          this.satelliteIDs.CHASER,
          Cesium.Color.RED
        )
      )

      if (this.nominalTargetPositions != null) {
        this.satellitesDS.entities.add(
          this.initSatEntity(
            this.nominalTargetPositions,
            this.julianDates,
            this.satelliteIDs.NOMINAL_TARGET,
            Cesium.Color.GREEN
          )
        )
        this.satellitesDS.entities.add(
          this.initSatEntity(
            this.afterManeuverTargetPositions,
            this.afterManeuverJulianDates,
            this.satelliteIDs.MANEUVERING_TARGET,
            Cesium.Color.BLUE
          )
        )
      } else {
        this.satellitesDS.entities.add(
          this.initSatEntity(
            this.maneuveringTargetPositions,
            this.julianDates,
            this.satelliteIDs.MANEUVERING_TARGET,
            Cesium.Color.GREEN
          )
        )
      }
    },
    resetSatellites() {
      this.isViewerLoading = true
      this.satellitesDS.entities.removeAll()
      this.initSatellites()
      this.setCesiumClock(this.julianStartDate, this.julianEndDate)
      this.isViewerLoading = false
    },
    initSatEntity(satPositions, sampleTimes, satName, color) {
      const sampledPositions = this.createPositionsProperty(
        satPositions,
        sampleTimes,
        Cesium.ReferenceFrame.INERTIAL
      )

      return new Cesium.Entity({
        id: satName,
        description: satName,
        position: sampledPositions,
        show: true,
        point: {
          pixelSize: 8,
          color: color,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 1
        },
        label: {
          text: satName,
          font: '16px Helvetica',
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          pixelOffset: new Cesium.Cartesian2(0, 20),
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          distanceDisplayCondition: new Cesium.DistanceDisplayCondition(
            5e4,
            3e7
          )
        }
      })
    },

    createPositionsProperty(
      positions,
      sampleTimes,
      referenceFrame = Cesium.ReferenceFrame.FIXED
    ) {
      const sampledPositionProperty = new Cesium.SampledPositionProperty(
        referenceFrame
      )
      sampledPositionProperty.setInterpolationOptions(
        Cesium.LagrangePolynomialApproximation,
        5
      )

      sampledPositionProperty.addSamples(
        sampleTimes,
        positions.map(
          (position) =>
            new Cesium.Cartesian3(position[0], position[1], position[2])
        )
      )

      return sampledPositionProperty
    },
    isEqualPosition(pos1, pos2) {
      return pos1[0] === pos2[0] && pos1[1] === pos2[1] && pos1[2] === pos2[2]
    },
    setTimeFromUTC(date) {
      this.viewer.clock.currentTime = Cesium.JulianDate.fromIso8601(date)
    }
  }
}
</script>

<style scoped>
#cesium-container {
  position: relative;
  height: 500px;
  width: 100%;
  z-index: 2;
}

#blurred-background {
  background-color: black;
  opacity: 0.5;
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: 3;
}

#loading-indicator {
  position: absolute;
  margin: auto;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 4;
  display: block;
}

.infobox {
  display: block;
  position: absolute;
  left: 20px;
  top: 20px;
  z-index: 9;
  background: white;
  color: blue;
  padding: 4px;
  border: 2px solid red;
  border-radius: 5px;
}
</style>
